import { CloseOutlined, CodeOutlined } from "@ant-design/icons";
import { Alert, Button, Card, Col, Row, Space, Typography, Segmented } from "antd";
import { BaseFilterItem } from "components/UI/BaseFilter/BaseFilterItem/BaseFilterItem.tsx";
import { FilterEditorContextProvider } from "components/UI/BaseFilter/FilterEditorContext.tsx";
import { FilterGroup } from "components/UI/BaseFilter/FilterGroup/FilterGroup.tsx";
import { FilterHelp } from "components/UI/BaseFilter/FilterHelp.tsx";
import { FilterEditorState } from "components/UI/BaseFilter/hooks/useFilterEditor.ts";
import { InsertControls } from "components/UI/BaseFilter/InsertControls/InsertControls.tsx";
import { createFilterFieldsCategory } from "components/UI/BaseFilter/utils/formStructureFieldsCategory.ts";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { lazy, Suspense, useMemo, useState } from "react";

import FormStructureData = App.Data.FormStructureData;

const FilterBlueprintDrawer = lazy(() => import("components/UI/BaseFilter/FilterBlueprintDrawer.tsx"));

interface BaseFilterProps {
    editor: FilterEditorState;
    /** Structures the "Leads data" picker offers: the integration's one (cascade) or the offer's ones (offer) */
    formStructures: FormStructureData[];
}

/** No filter is an empty editor rather than a "null" the user would have to delete first. */
const stringifyTree = (tree: unknown): string => (tree === null ? "" : JSON.stringify(tree, null, 2));

const prettyJson = (json: string): string => {
    try {
        return JSON.stringify(JSON.parse(json), null, 2);
    } catch {
        return json;
    }
};

export const BaseFilter = ({ editor, formStructures }: BaseFilterProps) => {
    const { t } = usePaldockTranslation("ui.offer-filter");
    const { t: tPicker } = usePaldockTranslation("components.connection-creator");
    const { root: entries, dispatch, context, unsupported, clearUnsupported, showErrors, errors } = editor;
    const isCascade = context === "cascade";
    const [blueprintOpen, setBlueprintOpen] = useState(false);

    const fieldsCategory = useMemo(
        () => createFilterFieldsCategory(formStructures, tPicker),
        [formStructures, tPicker]
    );

    const errorsByNode = useMemo(() => {
        const map = new Map<string, typeof errors>();
        errors.forEach((error) => map.set(error.nodeId, [...(map.get(error.nodeId) ?? []), error]));
        return map;
    }, [errors]);

    const blueprintText = useMemo(
        () =>
            editor.unsupportedSavedJson !== null
                ? prettyJson(editor.unsupportedSavedJson)
                : stringifyTree(editor.toTree()),

        [editor.unsupportedSavedJson, editor.toTree, blueprintOpen]
    );

    const hasAnyFilters = entries.length > 0;

    const cardClassName = isCascade
        ? "rounded-lg w-full border-none bg-[#00000005]"
        : "mt-4 rounded-lg w-full border-none bg-[#00000005]";

    const titleLevel = isCascade ? 5 : 4;
    const titleText = isCascade ? t("title_cascade") : t("title");

    const addFilter = (position?: number) => dispatch({ type: "addRule", context, position });
    const addGroup = (position?: number) => dispatch({ type: "addGroup", position });

    return (
        <Card className={cardClassName}>
            <Row justify="space-between" align="middle">
                <Col>
                    <Typography.Title level={titleLevel}>{titleText}</Typography.Title>
                </Col>
                <Col>
                    <Space>
                        <Button icon={<CodeOutlined />} onClick={() => setBlueprintOpen(true)}>
                            {t("blueprint.button")}
                        </Button>
                        {!unsupported && (
                            <Button icon={<CloseOutlined />} onClick={() => dispatch({ type: "clear" })}>
                                {t("labels.clear")}
                            </Button>
                        )}
                    </Space>
                </Col>
            </Row>

            <FilterHelp context={context} />

            {unsupported ? (
                <Alert
                    className="mt-4"
                    type="warning"
                    showIcon
                    message={t("unsupported.title")}
                    description={t("unsupported.message")}
                    action={
                        <Button danger onClick={clearUnsupported}>
                            {t("unsupported.clear")}
                        </Button>
                    }
                />
            ) : (
                <FilterEditorContextProvider value={{ context, dispatch, fieldsCategory, showErrors, errorsByNode }}>
                    <div className="mt-4 p-0">
                        {/* Insertion point at the beginning */}
                        {hasAnyFilters && (
                            <InsertControls
                                position="between"
                                onAddFilter={() => addFilter(0)}
                                onAddGroup={() => addGroup(0)}
                            />
                        )}

                        {entries.map((entry, index) => (
                            <div key={entry.item.id}>
                                <div className="mb-2">
                                    {entry.item.kind === "group" ? (
                                        <FilterGroup group={entry.item} />
                                    ) : (
                                        <BaseFilterItem rule={entry.item} />
                                    )}
                                </div>
                                {/* Show logical operator between items (not after the last one) */}
                                {index < entries.length - 1 && (
                                    <div className="flex justify-center my-4">
                                        <Segmented
                                            value={entry.join}
                                            onChange={(value) =>
                                                dispatch({
                                                    type: "updateJoin",
                                                    id: entry.item.id,
                                                    join: value as "and" | "or",
                                                })
                                            }
                                            options={[
                                                { label: t("labels.and"), value: "and" },
                                                { label: t("labels.or"), value: "or" },
                                            ]}
                                            style={{ width: "250px" }}
                                            className="[&_.ant-segmented-item]:!w-[125px]"
                                        />
                                    </div>
                                )}
                                {/* Insertion point after each item */}
                                <InsertControls
                                    position="between"
                                    onAddFilter={() => addFilter(index + 1)}
                                    onAddGroup={() => addGroup(index + 1)}
                                />
                            </div>
                        ))}

                        {/* If no items, show insertion controls in center */}
                        {!hasAnyFilters && (
                            <InsertControls
                                position="end"
                                onAddFilter={() => addFilter()}
                                onAddGroup={() => addGroup()}
                            />
                        )}
                    </div>
                </FilterEditorContextProvider>
            )}
            {blueprintOpen && (
                <Suspense fallback={null}>
                    <FilterBlueprintDrawer
                        open={blueprintOpen}
                        onClose={() => setBlueprintOpen(false)}
                        initialText={blueprintText}
                        context={context}
                        fieldTags={editor.fieldTags}
                        onApply={editor.applyBlueprint}
                    />
                </Suspense>
            )}
        </Card>
    );
};
